/**
 * Progress Monitor Utility
 * Handles Server-Sent Events for real-time progress updates
 */

class ProgressMonitor {
  constructor(sessionId, baseUrl = '/api') {
    this.sessionId = sessionId;
    this.baseUrl = baseUrl;
    this.eventSource = null;
    this.callbacks = {
      progress: [],
      complete: [],
      error: [],
      connected: []
    };
  }

  /**
   * Start monitoring progress for the session
   */
  start() {
    if (this.eventSource) {
      console.warn('Progress monitor already started');
      return;
    }

    const url = `${this.baseUrl}/progress/${this.sessionId}`;
    console.log('🔄 Starting progress monitor:', url);

    this.eventSource = new EventSource(url);

    this.eventSource.onopen = (event) => {
      console.log('✅ SSE connection opened', event);
    };

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📊 Progress update received:', data);

        // Call appropriate callbacks based on message type
        if (this.callbacks[data.type]) {
          this.callbacks[data.type].forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('Error in progress callback:', error);
            }
          });
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    this.eventSource.onerror = (event) => {
      console.error('❌ SSE connection error:', event);
      
      // Call error callbacks
      this.callbacks.error.forEach(callback => {
        try {
          callback({ 
            type: 'connection_error', 
            message: 'Connection to progress stream failed',
            event 
          });
        } catch (error) {
          console.error('Error in error callback:', error);
        }
      });
    };
  }

  /**
   * Stop monitoring progress
   */
  stop() {
    if (this.eventSource) {
      console.log('🛑 Stopping progress monitor');
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Add a callback for progress updates
   * @param {Function} callback - Function to call with progress data
   */
  onProgress(callback) {
    this.callbacks.progress.push(callback);
    return this; // For chaining
  }

  /**
   * Add a callback for completion
   * @param {Function} callback - Function to call when process completes
   */
  onComplete(callback) {
    this.callbacks.complete.push(callback);
    return this; // For chaining
  }

  /**
   * Add a callback for errors
   * @param {Function} callback - Function to call when an error occurs
   */
  onError(callback) {
    this.callbacks.error.push(callback);
    return this; // For chaining
  }

  /**
   * Add a callback for connection established
   * @param {Function} callback - Function to call when connection is established
   */
  onConnected(callback) {
    this.callbacks.connected.push(callback);
    return this; // For chaining
  }

  /**
   * Remove all callbacks of a specific type
   * @param {string} type - Type of callbacks to remove (progress, complete, error, connected)
   */
  removeCallbacks(type) {
    if (this.callbacks[type]) {
      this.callbacks[type] = [];
    }
    return this;
  }

  /**
   * Remove all callbacks
   */
  removeAllCallbacks() {
    Object.keys(this.callbacks).forEach(type => {
      this.callbacks[type] = [];
    });
    return this;
  }

  /**
   * Get connection status
   */
  isConnected() {
    return this.eventSource && this.eventSource.readyState === EventSource.OPEN;
  }

  /**
   * Get connection state
   */
  getState() {
    if (!this.eventSource) {
      return 'not_started';
    }
    
    switch (this.eventSource.readyState) {
      case EventSource.CONNECTING:
        return 'connecting';
      case EventSource.OPEN:
        return 'connected';
      case EventSource.CLOSED:
        return 'closed';
      default:
        return 'unknown';
    }
  }
}

// Export for both CommonJS and ES modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProgressMonitor;
} else if (typeof window !== 'undefined') {
  window.ProgressMonitor = ProgressMonitor;
}
